'use client';

import { useState, useEffect } from 'react';
import CapabilityCard from './CapabilityCard';
import FilterPanel, { SelectedFilters } from './FilterPanel';
import ComparisonMatrix from './ComparisonMatrix';
import VideoDemo from './VideoDemo';
import Icon from '@/components/ui/AppIcon';

interface CapabilityData {
  id: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
  specifications: Array<{label: string;value: string;}>;
  applications: string[];
  certifications: Array<{name: string;icon: string;}>;
  caseStudy: {
    title: string;
    description: string;
    outcome: string;
  };
  capacityStatus: 'high' | 'medium' | 'low';
  downloadUrl: string;
  category: string;
  materials: string[];
}

const CapabilitiesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeView, setActiveView] = useState<'grid' | 'comparison'>('grid');
  const [filteredCapabilities, setFilteredCapabilities] = useState<CapabilityData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedCapabilityId, setSelectedCapabilityId] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const capabilities: CapabilityData[] = [
  {
    id: 'cnc-precision',
    title: 'CNC Precision Machining',
    description: 'Ultra-precision 5-axis CNC machining for complex aerospace components with tolerances down to ±0.0001 inches. Ideal for critical flight hardware and satellite structures.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ad227694-1766493775758.png",
    alt: 'Industrial CNC machine with precision cutting tool working on metallic aerospace component',
    icon: 'CogIcon',
    specifications: [
    { label: 'Tolerance', value: '±0.0001"' },
    { label: 'Max Size', value: '60" x 40" x 30"' },
    { label: 'Axes', value: '5-Axis Simultaneous' },
    { label: 'Surface Finish', value: 'Ra 8-32 µin' }],

    applications: [
    'Satellite structural components',
    'Launch vehicle interface hardware',
    'Precision optical mounts',
    'Flight control mechanisms',
    'Propulsion system components'],

    certifications: [
    { name: 'AS9100', icon: 'ShieldCheckIcon' },
    { name: 'ITAR', icon: 'LockClosedIcon' },
    { name: 'NADCAP', icon: 'CheckBadgeIcon' }],

    caseStudy: {
      title: 'Satellite Deployment Mechanism',
      description: 'Manufactured critical deployment mechanism for LEO satellite constellation requiring extreme precision and reliability in space environment.',
      outcome: '100% mission success rate across 50+ deployed units with zero failures in orbit'
    },
    capacityStatus: 'high',
    downloadUrl: '/specs/cnc-machining.pdf',
    category: 'cnc',
    materials: ['aluminum', 'titanium', 'stainless']
  },
  {
    id: 'additive-metal',
    title: 'Additive Manufacturing',
    description: 'Advanced DMLS and EBM processes for complex geometries impossible with traditional manufacturing. Aerospace-grade materials with full material certification.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1887738a0-1773326254011.png",
    alt: 'Metal 3D printer creating intricate aerospace component layer by layer with laser fusion',
    icon: 'CubeIcon',
    specifications: [
    { label: 'Technology', value: 'DMLS / EBM' },
    { label: 'Build Volume', value: '250mm x 250mm x 325mm' },
    { label: 'Layer Thickness', value: '20-100 µm' },
    { label: 'Density', value: '>99.5%' }],

    applications: [
    'Lightweight structural brackets',
    'Conformal cooling channels',
    'Topology-optimized components',
    'Rapid prototyping for flight hardware',
    'Low-volume production parts'],

    certifications: [
    { name: 'AS9100', icon: 'ShieldCheckIcon' },
    { name: 'ITAR', icon: 'LockClosedIcon' }],

    caseStudy: {
      title: 'Rocket Engine Injector',
      description: 'Designed and manufactured complex fuel injector with integrated cooling channels for next-generation rocket engine.',
      outcome: '40% weight reduction and 25% performance improvement over traditional design'
    },
    capacityStatus: 'medium',
    downloadUrl: '/specs/additive-manufacturing.pdf',
    category: 'additive',
    materials: ['titanium', 'aluminum', 'specialty']
  },
  {
    id: 'sheet-metal',
    title: 'Aerospace Sheet Metal',
    description: 'Precision sheet metal fabrication for aircraft structures, enclosures, and brackets. Advanced forming, welding, and finishing capabilities.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1247e180d-1766493775753.png",
    alt: 'Precision sheet metal bending machine forming aerospace-grade aluminum panel',
    icon: 'RectangleStackIcon',
    specifications: [
    { label: 'Material Thickness', value: '0.016" - 0.250"' },
    { label: 'Bend Radius', value: 'Down to 1T' },
    { label: 'Max Size', value: '120" x 60"' },
    { label: 'Welding', value: 'TIG, MIG, Spot' }],

    applications: [
    'Aircraft interior panels',
    'Avionics enclosures',
    'Structural brackets and fittings',
    'Fuel tank components',
    'Environmental control ducting'],

    certifications: [
    { name: 'AS9100', icon: 'ShieldCheckIcon' },
    { name: 'NADCAP', icon: 'CheckBadgeIcon' }],

    caseStudy: {
      title: 'UAV Airframe Components',
      description: 'Complete sheet metal fabrication for unmanned aerial vehicle airframe including complex formed panels and welded assemblies.',
      outcome: 'Delivered 500+ assemblies with 99.8% first-pass quality rate'
    },
    capacityStatus: 'high',
    downloadUrl: '/specs/sheet-metal.pdf',
    category: 'sheet-metal',
    materials: ['aluminum', 'stainless']
  },
  {
    id: 'composites',
    title: 'Advanced Composites',
    description: 'Carbon fiber and advanced composite manufacturing for lightweight, high-strength aerospace structures. Autoclave and out-of-autoclave processes.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12f616565-1766493775701.png",
    alt: 'Carbon fiber composite material being laid up on aerospace mold with precision alignment',
    icon: 'Square3Stack3DIcon',
    specifications: [
    { label: 'Materials', value: 'Carbon Fiber, Kevlar, Fiberglass' },
    { label: 'Process', value: 'Autoclave, OOA, Prepreg' },
    { label: 'Max Size', value: '72" x 48" x 24"' },
    { label: 'Fiber Volume', value: 'Up to 65%' }],

    applications: [
    'Satellite structural panels',
    'UAV wings and fuselage',
    'Radome structures',
    'Lightweight brackets and fittings',
    'Thermal protection systems'],

    certifications: [
    { name: 'AS9100', icon: 'ShieldCheckIcon' },
    { name: 'NADCAP', icon: 'CheckBadgeIcon' }],

    caseStudy: {
      title: 'Satellite Solar Panel Structure',
      description: 'Manufactured ultra-lightweight carbon fiber structure for deployable solar panels on commercial satellite.',
      outcome: '60% weight savings compared to aluminum with superior stiffness-to-weight ratio'
    },
    capacityStatus: 'medium',
    downloadUrl: '/specs/composites.pdf',
    category: 'composites',
    materials: ['composites']
  },
  {
    id: 'testing-validation',
    title: 'Testing & Validation',
    description: 'Comprehensive testing services including dimensional inspection, material testing, non-destructive testing, and environmental qualification.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d2e85d24-1772129740009.png",
    alt: 'Precision coordinate measuring machine inspecting aerospace component with laser probe',
    icon: 'BeakerIcon',
    specifications: [
    { label: 'CMM Accuracy', value: '±0.0001"' },
    { label: 'NDT Methods', value: 'X-Ray, UT, PT, MT' },
    { label: 'Environmental', value: 'Thermal, Vibration, Shock' },
    { label: 'Material Testing', value: 'Tensile, Hardness, Metallography' }],

    applications: [
    'First article inspection',
    'In-process quality verification',
    'Material certification',
    'Failure analysis',
    'Environmental qualification testing'],

    certifications: [
    { name: 'AS9100', icon: 'ShieldCheckIcon' },
    { name: 'NADCAP', icon: 'CheckBadgeIcon' },
    { name: 'ISO 17025', icon: 'DocumentCheckIcon' }],

    caseStudy: {
      title: 'Launch Vehicle Component Qualification',
      description: 'Complete testing and qualification program for critical launch vehicle structural components including environmental testing.',
      outcome: 'Successfully qualified components for human-rated spaceflight with full traceability'
    },
    capacityStatus: 'high',
    downloadUrl: '/specs/testing-validation.pdf',
    category: 'testing',
    materials: ['aluminum', 'titanium', 'stainless', 'composites', 'specialty']
  },
  {
    id: 'surface-treatment',
    title: 'Surface Treatment & Finishing',
    description: 'Aerospace-approved surface treatments including anodizing, chemical film, passivation, and specialty coatings for corrosion protection and performance.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11a2379b0-1766493776264.png",
    alt: 'Aerospace component being treated in anodizing bath with precision temperature control',
    icon: 'SparklesIcon',
    specifications: [
    { label: 'Anodizing', value: 'Type II, Type III' },
    { label: 'Chem Film', value: 'MIL-DTL-5541' },
    { label: 'Passivation', value: 'AMS 2700' },
    { label: 'Coating', value: 'Thermal Spray, PVD' }],

    applications: [
    'Corrosion protection for aluminum parts',
    'Wear resistance for moving components',
    'Electrical insulation',
    'Thermal barrier coatings',
    'Aesthetic finishing'],

    certifications: [
    { name: 'AS9100', icon: 'ShieldCheckIcon' },
    { name: 'NADCAP', icon: 'CheckBadgeIcon' }],

    caseStudy: {
      title: 'Satellite Thermal Control Coating',
      description: 'Applied specialized thermal control coating to satellite components for optimal heat dissipation in space environment.',
      outcome: 'Achieved target thermal emissivity with 15-year space environment durability'
    },
    capacityStatus: 'high',
    downloadUrl: '/specs/surface-treatment.pdf',
    category: 'sheet-metal',
    materials: ['aluminum', 'titanium', 'stainless']
  }];


  const comparisonData = capabilities.map((cap) => ({
    id: cap.id,
    title: cap.title,
    icon: cap.icon,
    features: {
      tolerance: cap.specifications[0]?.value || 'N/A',
      materials: cap.materials.length + ' types',
      complexity: cap.applications.length > 4 ? 'High' : 'Medium',
      volume: cap.capacityStatus === 'high' ? 'High Volume' : cap.capacityStatus === 'medium' ? 'Medium Volume' : 'Low Volume',
      leadTime: cap.capacityStatus === 'high' ? '2-4 weeks' : cap.capacityStatus === 'medium' ? '4-6 weeks' : '6-8 weeks',
      cost: cap.category === 'additive' ? '$$$' : cap.category === 'composites' ? '$$$$' : '$$'
    }
  }));

  const videoData = {
    id: 'cnc-process',
    title: 'CNC Machining Process Overview',
    description: 'Watch our precision 5-axis CNC machining process in action, from raw material to finished aerospace component.',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1ad227694-1766493775758.png",
    alt: 'CNC machine in operation showing precision cutting of aerospace component',
    duration: '3:45',
    overlays: [
    {
      time: '0:30',
      title: 'Material Setup',
      description: 'Aerospace-grade aluminum billet secured in precision fixture'
    },
    {
      time: '1:45',
      title: 'Roughing Operation',
      description: 'High-speed roughing removes bulk material with optimal tool paths'
    },
    {
      time: '2:30',
      title: 'Finishing Pass',
      description: 'Final precision passes achieve ±0.0001" tolerance requirements'
    }]

  };

  useEffect(() => {
    if (isHydrated) {
      setFilteredCapabilities(capabilities);
    }
  }, [isHydrated]);

  const handleFilterChange = (filters: SelectedFilters) => {
    if (!isHydrated) return;

    let filtered = capabilities;

    if (filters.categories.length > 0) {
      filtered = filtered.filter((cap) => filters.categories.includes(cap.category));
    }

    if (filters.materials.length > 0) {
      filtered = filtered.filter((cap) =>
      cap.materials.some((mat) => filters.materials.includes(mat))
      );
    }

    if (filters.capacity.length > 0) {
      filtered = filtered.filter((cap) => filters.capacity.includes(cap.capacityStatus));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((cap) =>
      cap.title.toLowerCase().includes(query) ||
      cap.description.toLowerCase().includes(query) ||
      cap.applications.some((app) => app.toLowerCase().includes(query))
      );
    }

    setFilteredCapabilities(filtered);
  };

  const handleSearch = (query: string) => {
    if (!isHydrated) return;
    setSearchQuery(query);
    handleFilterChange({
      categories: [],
      materials: [],
      certifications: [],
      capacity: []
    });
  };

  const handleRequestQuote = (capabilityId: string) => {
    if (!isHydrated) return;
    setSelectedCapabilityId(capabilityId);
    setShowQuoteModal(true);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-cta text-sm text-technical-gray">Loading capabilities...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/20 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12 bg-[rgba(247,105,38,0)]">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full border border-accent/30 mb-6">
              <Icon name="RocketLaunchIcon" size={20} className="text-accent" />
              <span className="font-cta text-xs text-accent uppercase tracking-wider">
                Manufacturing Excellence
              </span>
            </div>
            <h1 className="font-headline text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Our Manufacturing Ecosystem
            </h1>
            <p className="font-body text-xl max-w-3xl mx-auto leading-relaxed text-[rgba(250,129,30,1)] font-medium text-center">
              Integrated manufacturing solutions for aerospace, defense, space, energy, automotive, and industrial sectors—bringing together engineering, precision manufacturing, quality assurance, and supply chain execution on a single platform.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Icon
                name="MagnifyingGlassIcon"
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-technical-gray" />

              <input
                type="text"
                placeholder="Search capabilities, materials, or applications..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-card border border-border rounded-lg font-body text-sm text-foreground placeholder-technical-gray focus:outline-none focus:border-accent transition-colors duration-300" />

            </div>
          </div>

          {/* View Toggle */}
          <div className="flex justify-center space-x-2 mb-8">
            <button
              onClick={() => setActiveView('grid')}
              className={`px-6 py-3 rounded-lg font-cta text-sm transition-all duration-300 ${
              activeView === 'grid' ? 'bg-accent text-primary' : 'bg-muted text-technical-gray hover:text-foreground'}`
              }>

              <Icon name="Squares2X2Icon" size={20} className="inline mr-2" />
              GRID VIEW
            </button>
            <button
              onClick={() => setActiveView('comparison')}
              className={`px-6 py-3 rounded-lg font-cta text-sm transition-all duration-300 ${
              activeView === 'comparison' ? 'bg-accent text-primary' : 'bg-muted text-technical-gray hover:text-foreground'}`
              }>

              <Icon name="TableCellsIcon" size={20} className="inline mr-2" />
              COMPARISON
            </button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filter Panel */}
            <aside className="lg:w-80 flex-shrink-0">
              <FilterPanel onFilterChange={handleFilterChange} />
            </aside>

            {/* Content Area */}
            <div className="flex-1">
              {activeView === 'grid' ?
              <>
                  {/* Results Header */}
                  <div className="flex items-center justify-between mb-6">
                    <p className="font-body text-sm text-technical-gray">
                      Showing {filteredCapabilities.length} of {capabilities.length} capabilities
                    </p>
                  </div>

                  {/* Capabilities Grid */}
                  <div className="grid md:grid-cols-2 gap-6 mb-12">
                    {filteredCapabilities.map((capability) =>
                  <CapabilityCard
                    key={capability.id}
                    capability={capability}
                    onRequestQuote={handleRequestQuote} />

                  )}
                  </div>

                  {/* Video Demo Section */}
                  <div className="mb-12">
                    <h2 className="font-headline text-3xl font-bold text-foreground mb-6">
                      Process Demonstrations
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <VideoDemo video={videoData} />
                      <VideoDemo
                      video={{
                        ...videoData,
                        id: 'additive-process',
                        title: 'Metal Additive Manufacturing',
                        description: 'See how we build complex aerospace components layer by layer with DMLS technology.',
                        thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1beeb75ea-1765300594171.png",
                        alt: 'Metal 3D printer building aerospace component with laser fusion process'
                      }} />

                    </div>
                  </div>

                  {/* Empty State */}
                  {filteredCapabilities.length === 0 &&
                <div className="text-center py-20">
                      <Icon name="FunnelIcon" size={64} className="text-technical-gray mx-auto mb-4 opacity-50" />
                      <h3 className="font-headline text-2xl font-bold text-foreground mb-2">
                        No Capabilities Found
                      </h3>
                      <p className="font-body text-sm text-technical-gray mb-6">
                        Try adjusting your filters or search query
                      </p>
                      <button
                    onClick={() => {
                      setSearchQuery('');
                      handleFilterChange({
                        categories: [],
                        materials: [],
                        certifications: [],
                        capacity: []
                      });
                    }}
                    className="px-6 py-3 bg-accent text-primary font-cta text-sm rounded-lg hover:shadow-glow transition-all duration-300">

                        CLEAR ALL FILTERS
                      </button>
                    </div>
                }
                </> :

              <ComparisonMatrix capabilities={comparisonData} />
              }
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-secondary to-primary rounded-2xl p-12 text-center border border-accent/30 shadow-aerospace">
            <Icon name="RocketLaunchIcon" size={48} className="text-accent mx-auto mb-6" />
            <h2 className="font-headline text-3xl font-bold text-foreground mb-4">
              Ready to Start Your Mission?
            </h2>
            <p className="font-body text-lg text-technical-gray mb-8 max-w-2xl mx-auto">
              Our aerospace manufacturing experts are standing by to discuss your project requirements and provide technical consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowQuoteModal(true)}
                className="px-8 py-4 bg-mission-orange text-primary font-cta text-sm rounded-lg hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5">

                REQUEST CAPABILITY ASSESSMENT
              </button>
              <a
                href="/support"
                className="px-8 py-4 bg-card text-foreground font-cta text-sm rounded-lg border border-border hover:border-accent transition-all duration-300">

                CONTACT ENGINEERING TEAM
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Modal */}
      {showQuoteModal &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-primary/80 backdrop-blur-sm">
          <div className="bg-card rounded-lg border border-border max-w-md w-full p-6 shadow-aerospace">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-headline text-xl font-bold text-foreground">
                Request Quote
              </h3>
              <button
              onClick={() => setShowQuoteModal(false)}
              className="p-2 hover:bg-muted rounded-lg transition-colors duration-300">

                <Icon name="XMarkIcon" size={20} className="text-foreground" />
              </button>
            </div>
            <p className="font-body text-sm text-technical-gray mb-6">
              Our team will review your requirements and provide a detailed quote within 24 hours.
            </p>
            <div className="space-y-4">
              <input
              type="text"
              placeholder="Your Name"
              className="w-full px-4 py-3 bg-muted border border-border rounded-lg font-body text-sm text-foreground placeholder-technical-gray focus:outline-none focus:border-accent transition-colors duration-300" />

              <input
              type="email"
              placeholder="Email Address"
              className="w-full px-4 py-3 bg-muted border border-border rounded-lg font-body text-sm text-foreground placeholder-technical-gray focus:outline-none focus:border-accent transition-colors duration-300" />

              <textarea
              placeholder="Project Requirements"
              rows={4}
              className="w-full px-4 py-3 bg-muted border border-border rounded-lg font-body text-sm text-foreground placeholder-technical-gray focus:outline-none focus:border-accent transition-colors duration-300 resize-none">
            </textarea>
              <button className="w-full px-6 py-3 bg-mission-orange text-primary font-cta text-sm rounded-lg hover:shadow-glow transition-all duration-300">
                SUBMIT REQUEST
              </button>
            </div>
          </div>
        </div>
      }
    </div>);

};

export default CapabilitiesInteractive;